import React, { useEffect, useRef, useState } from 'react';
import shotTagGroups from '../../assets/images/app-screenshots/tag-group-system.png';
import shotShareGaps from '../../assets/images/app-screenshots/share-gaps-to-groups.png';
import shotBroadcast from '../../assets/images/app-screenshots/broadcast-messages-to-groups.png';
import shotFeedback from '../../assets/images/app-screenshots/feedback.png';
import shotSchoolMode from '../../assets/images/app-screenshots/school-mode.png';
import shotDiaryTypes from '../../assets/images/app-screenshots/diary-types-rates.png';
import shotPupilApp from '../../assets/images/app-screenshots/pupil-app.png';
import shotSyllabus from '../../assets/images/app-screenshots/syllabus.png';
import shotMoney from '../../assets/images/app-screenshots/money.png';
import shotReminders from '../../assets/images/app-screenshots/lesson-reminders.png';
import shotCustomisation from '../../assets/images/app-screenshots/customisation.png';
import shotSecurity from '../../assets/images/app-screenshots/security.png';
import shotActivityLog from '../../assets/images/app-screenshots/activity-log.png';
import shotTodos from '../../assets/images/app-screenshots/todos.png';
import shotLessons from '../../assets/images/app-screenshots/lessons-in-list-format.png';
import shotNotifications from '../../assets/images/app-screenshots/notifications.png';
import shapeSix from '../../assets/images/shape/shape-6.png';
import shapeSeven from '../../assets/images/shape/shape-7.png';
import shapeEight from '../../assets/images/shape/shape-8.png';
import { IOS_APP_URL, ANDROID_APP_URL } from '../../storeLinks';

// hidden: true keeps an entry out of the tabs without deleting it
const FEATURES = [
    {
        key: 'grouping',
        image: shotTagGroups,
        icon: 'layer-group',
        label: 'Pupil Grouping',
        title: 'Organise pupils your way',
        text: "Tag pupils by location, transmission or anything you like, such as 'Test ready' or 'Nervous pupil'. Built-in groups like 'Test within 30 days' and 'Owes money' keep themselves up to date, and when you post a gap only the pupils it suits are alerted.",
    },
    {
        key: 'gaps',
        image: shotShareGaps,
        icon: 'calendar',
        label: 'Fill Diary Gaps',
        title: 'Fill cancellations fast',
        text: 'Got a free slot? Post it as a gap and choose which groups to send it to, such as pupils in Salford. They are alerted straight away and can book it from their app, so the slot is filled without a single phone call.',
    },
    {
        key: 'broadcast',
        image: shotBroadcast,
        icon: 'paper-plane',
        label: 'Broadcast Messages',
        title: 'One message, the right pupils',
        text: "Send a message to whole groups at once, or pick individual pupils. Let your Salford pupils know about new availability, or remind everyone who's test ready, all in a few taps.",
    },
    {
        key: 'feedback',
        image: shotFeedback,
        icon: 'comment-dots',
        label: 'Lesson Feedback',
        title: 'Feedback your pupils actually read',
        text: 'Write up each lesson in seconds, add photos and tap in the weather and road conditions. Pupils see it in their app, then reflect on the lesson by answering your questions and rating it out of five.',
    },
    {
        key: 'school',
        hidden: true,
        image: shotSchoolMode,
        icon: 'users',
        label: 'Multi School',
        title: 'One school, every instructor',
        text: "Invite instructors to your school and share one subscription. Owners and admins see the whole school's diary and filter it by instructor, while each instructor keeps control of their own pupils and lessons.",
    },
    {
        key: 'rates',
        image: shotDiaryTypes,
        icon: 'tag',
        label: 'Lesson Types & Rates',
        title: 'The right price for every lesson',
        text: "Set up standard, evening and weekend lessons, mock tests and more, each with its own rate. Pupils' credit is charged at the right rate automatically, so a pricier lesson simply uses a little more of their hours.",
    },
    {
        key: 'pupil-app',
        image: shotPupilApp,
        icon: 'mobile',
        label: 'Pupil App',
        title: 'A free app for your pupils',
        text: 'Invite pupils by mobile number and they sign in with a text code. They see their next lesson, credit, progress and lesson notes, message you, and can book the gaps you post.',
    },
    {
        key: 'syllabus',
        image: shotSyllabus,
        icon: 'book',
        label: 'Syllabus & Comments',
        title: 'Progress at a glance',
        text: 'Score every skill from 1 to 5 as you go, with colour-coded progress and comments your pupil can read. Use the standard syllabus or build your own.',
    },
    {
        key: 'money',
        image: shotMoney,
        icon: 'money-bill',
        label: 'Money Tracking',
        title: "Know who's paid and who owes",
        text: 'Record payments and expenses in a couple of taps, mark lessons paid straight from the diary and set when payment is due. Download a tax-year summary when your self assessment comes round.',
    },
    {
        key: 'reminders',
        image: shotReminders,
        icon: 'alarm-clock',
        label: 'Reminders',
        title: 'Fewer missed lessons',
        text: 'Lesson reminders go out automatically before every lesson by push notification, text, or both. Choose how far ahead they are sent and add your own note.',
    },
    {
        key: 'customisation',
        image: shotCustomisation,
        padded: true,
        icon: 'palette',
        label: 'Customisation',
        title: 'Make it yours',
        text: 'Pick your school colour, choose how pupil names, lesson types and paid status show in your diary, and decide how lessons snap into place. Drive.Day fits the way you work.',
    },
    {
        key: 'gdpr',
        image: shotSecurity,
        padded: true,
        icon: 'shield-halved',
        label: 'GDPR',
        title: 'Data protection built in',
        text: "Your school's data is stored on servers in London. Pupils can view and download their own data, and you can export everything or delete your account at any time.",
    },
    {
        key: 'security',
        image: shotActivityLog,
        padded: true,
        icon: 'lock',
        label: 'Security',
        title: 'Secure by default',
        text: 'Every login is confirmed with a one-time code sent to your phone. See every signed-in device and sign any of them out remotely, while a full audit log shows who did what in your business.',
    },
    {
        key: 'todos',
        image: shotTodos,
        icon: 'circle-check',
        label: 'Todos',
        title: 'Nothing slips through',
        text: 'To-dos are built right into your diary. Drive.Day adds one automatically after every lesson, so you never forget to leave feedback.',
    },
    {
        key: 'lessons',
        hidden: true,
        image: shotLessons,
        icon: 'list',
        label: 'Lessons',
        title: 'Every lesson in one list',
        text: "Prefer a list to a calendar? See all your lessons in order, with who, when, whether they're paid and whether feedback has been left, plus your upcoming, delivered and cancelled totals.",
    },
    {
        key: 'notifications',
        image: shotNotifications,
        icon: 'bell',
        label: 'Notifications',
        title: 'Know what is happening',
        text: 'Get notified in the app whenever something changes in your business: new enquiries, gap bookings, cancellation requests, test dates and pupil reflections.',
    },
];
const VISIBLE_FEATURES = FEATURES.filter((feature) => !feature.hidden);

function FeaturesHomeOne({ className }) {
    const [tab, setTab] = useState(VISIBLE_FEATURES[0].key);
    const handleClick = (e, value) => {
        e.preventDefault();
        setTab(value);
    };
    // mobile: each pane has its own chip strip; keep the active chip in view
    // by scrolling the strip sideways (not the page)
    const stripRefs = useRef({});
    useEffect(() => {
        const strip = stripRefs.current[tab];
        const chip = strip && strip.querySelector('.dd-feature-chip.active');
        if (chip) {
            strip.scrollLeft = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
        }
    }, [tab]);
    return (
        <section className={`dd-features-area pt-100 ${className}`} id="features">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-3 d-none d-lg-block">
                        <div className="dd-features-tabs-btn">
                            <div
                                className="nav flex-column nav-pills"
                                id="v-pills-tab"
                                role="tablist"
                                aria-orientation="vertical"
                            >
                                {VISIBLE_FEATURES.map((feature) => (
                                    <a
                                        key={feature.key}
                                        onClick={(e) => handleClick(e, feature.key)}
                                        className={`nav-link ${tab === feature.key ? 'active' : ''}`}
                                        id={`v-pills-${feature.key}-tab`}
                                        data-toggle="pill"
                                        href={`#v-pills-${feature.key}`}
                                        role="tab"
                                        aria-controls={`v-pills-${feature.key}`}
                                        aria-selected={tab === feature.key}
                                    >
                                        <i className={`fa-jelly-fill fa-regular fa-${feature.icon}`} />{' '}
                                        {feature.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-9">
                        <div className="tab-content" id="v-pills-tabContent">
                            {VISIBLE_FEATURES.map((feature) => (
                                <div
                                    key={feature.key}
                                    className={`${
                                        tab === feature.key ? 'show active' : ''
                                    } tab-pane fade`}
                                    id={`v-pills-${feature.key}`}
                                    role="tabpanel"
                                    aria-labelledby={`v-pills-${feature.key}-tab`}
                                >
                                    <div className="row align-items-center">
                                        <div className="col-lg-6">
                                            <div
                                                className="dd-features-thumb text-center wow animated fadeInUp"
                                                data-wow-duration="2000ms"
                                                data-wow-delay="200ms"
                                            >
                                                <div className={feature.padded ? 'dd-shot-padded' : 'dd-shot'}>
                                                    <img src={feature.image} alt={`Drive.Day app: ${feature.label}`} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 d-lg-none">
                                            <div
                                                className="dd-feature-strip"
                                                ref={(el) => {
                                                    stripRefs.current[feature.key] = el;
                                                }}
                                                role="tablist"
                                                aria-label="Features"
                                            >
                                                {VISIBLE_FEATURES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.key}
                                                        className={`dd-feature-chip ${tab === item.key ? 'active' : ''}`}
                                                        onClick={() => setTab(item.key)}
                                                        role="tab"
                                                        aria-selected={tab === item.key}
                                                    >
                                                        <i className={`fa-jelly-fill fa-regular fa-${item.icon}`} />
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div
                                                className="dd-features-content wow animated fadeInRight"
                                                data-wow-duration="2000ms"
                                                data-wow-delay="600ms"
                                            >
                                                <span>{feature.label}</span>
                                                <h3 className="title">{feature.title}</h3>
                                                <p>{feature.text}</p>
                                                <ul className="dd-store-btns">
                                                    <li>
                                                        <a href={IOS_APP_URL}>
                                                            <i className="fab fa-apple" /> Download for iOS
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="item-2" href={ANDROID_APP_URL}>
                                                            <i className="fab fa-google-play" /> Download for Android
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="features-shape-1">
                <img src={shapeSix} alt="" />
            </div>
            <div className="features-shape-2">
                <img src={shapeSeven} alt="" />
            </div>
            <div className="features-shape-3">
                <img src={shapeEight} alt="" />
            </div>
        </section>
    );
}

export default FeaturesHomeOne;
